import React, { useEffect, useState } from 'react';
import './KiloHubAnimation.css';

interface KiloHubAnimationProps {
  isLoaded: boolean;
  onClick: () => void;
  className?: string;
}

const KiloHubAnimation: React.FC<KiloHubAnimationProps> = ({ 
  isLoaded, 
  onClick, 
  className = '' 
}) => {
  const [currentFrame, setCurrentFrame] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState<HTMLImageElement[]>([]);

  // Preload all images when component mounts
  useEffect(() => {
    const preloadImages = async () => {
      const images: HTMLImageElement[] = [];
      const loadPromises = [];

      for (let i = 1; i <= 24; i++) {
        const img = new Image();
        const frameNumber = i.toString().padStart(5, '0');
        img.src = `/images/ani_kilohub/Animation KILOBA_${frameNumber}.png`;
        
        const loadPromise = new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Continue even if image fails to load
        });
        
        images.push(img);
        loadPromises.push(loadPromise);
      }

      await Promise.all(loadPromises);
      setPreloadedImages(images);
    };

    preloadImages();
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    // Start animation
    setIsAnimating(true);
    
    let animationId: number;
    let lastFrameTime = 0;
    const frameInterval = 83; // ~12 FPS
    
    const animate = (currentTime: number) => {
      if (currentTime - lastFrameTime >= frameInterval) {
        setCurrentFrame(prev => {
          const nextFrame = prev >= 24 ? 1 : prev + 1;
          return nextFrame;
        });
        lastFrameTime = currentTime;
      }
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isLoaded]);

  const getFrameUrl = (frameNumber: number) => {
    const paddedNumber = frameNumber.toString().padStart(5, '0');
    return `/images/ani_kilohub/Animation KILOBA_${paddedNumber}.png`;
  };

  const fallbackImageUrl = '/images/ani_kilohub/Animation KILOBA_00001.png';

  return (
    <div 
      className={`kilohub-animation ${className} ${!isLoaded ? 'loading' : 'loaded'}`}
      onClick={onClick}
    >
      {isLoaded && isAnimating ? (
        <img 
          src={getFrameUrl(currentFrame)} 
          alt="KILO HUB Animation" 
          className="animation-frame"
          onError={(e) => {
            // Fallback to first frame if current frame fails to load
            e.currentTarget.src = fallbackImageUrl;
          }}
        />
      ) : (
        // Show static first frame while loading or if animation fails
        <img 
          src={fallbackImageUrl} 
          alt="KILO HUB" 
          className="animation-frame static"
        />
      )}
      
      {!isLoaded && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default KiloHubAnimation;